import Seo from "@/components/base/Seo";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/base/BackToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import { hotArticles } from "@/mocks/articles";

const toc = [
  { id: "dongdong-meaning", label: "洞洞含义：不只是可爱称呼" },
  { id: "shop-space", label: "杂货铺的空间设定" },
  { id: "time-rules", label: "时间规则与慢一小时的钟" },
  { id: "character-links", label: "角色关系如何被洞洞串联" },
  { id: "hidden-clues", label: "隐藏在角落的线索" },
];

const articleLd = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "洞洞杂货铺漫画世界观解析：洞洞含义与杂货铺设定全解",
  description:
    "从洞洞含义到杂货铺设定，全面解析洞洞杂货铺漫画的独特世界观、空间法则、时间规则、角色关系与隐藏线索。",
  datePublished: "2026-08-18",
  author: { "@type": "Organization", name: "洞洞杂货铺世界观解析站" },
};

const breadcrumbLd = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "首页", item: "https://comworld1.github.io/" },
    {
      "@type": "ListItem",
      position: 2,
      name: "世界观解析",
      item: "https://comworld1.github.io/worldview",
    },
  ],
};

export default function WorldView() {
  return (
    <>
      <Seo
        title="洞洞杂货铺漫画世界观解析 - 洞洞含义与杂货铺设定详解"
        description="洞洞杂货铺漫画的世界观是什么？本文从洞洞含义、杂货铺空间设定、时间规则、角色关系到隐藏线索，完整解析这部作品的独特世界观与故事背景。"
        keywords="洞洞杂货铺世界观,洞洞含义,洞洞杂货铺设定,洞洞杂货铺故事背景"
        canonicalPath="/worldview"
        type="article"
        jsonLd={[articleLd, breadcrumbLd]}
      />
      <Navbar />
      <main>
        <div className="mx-auto w-full max-w-6xl px-4 pt-8 md:px-6">
          <Breadcrumb items={[{ label: "世界观解析" }]} />
        </div>

        <article className="mx-auto w-full max-w-6xl px-4 py-8 md:px-6 md:py-10">
          <div className="max-w-3xl">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-primary-100 px-2.5 py-1 text-xs font-medium text-primary-800">
                洞洞含义
              </span>
              <span className="rounded-full bg-accent-100 px-2.5 py-1 text-xs font-medium text-accent-900">
                核心设定
              </span>
            </div>
            <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
              洞洞杂货铺漫画世界观解析：从洞洞含义到杂货铺设定
            </h1>
            <p className="mt-4 text-sm leading-7 text-foreground-700 md:text-base">
              想读懂《洞洞杂货铺》，最关键的一步是搞明白「洞洞」到底意味着什么。
              在这篇文章里，我们把整部作品的世界观拆成五块来看：洞洞的隐喻、
              杂货铺的空间、时间规则、角色关系，以及散落在画面里的隐藏线索。
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-3 text-xs text-foreground-600">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-user-smile-line"></i>
                编辑：洞洞考据组
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                2026-08-18
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                约 10 分钟阅读
              </span>
            </div>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_260px]">
            <div className="min-w-0">
              <div className="flex flex-col gap-10">
                <section id="dongdong-meaning" className="scroll-mt-24">
                  <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                    洞洞含义：不只是可爱称呼
                  </h2>
                  <p className="mt-4 text-sm leading-8 text-foreground-700 md:text-base">
                    「洞洞」是这部作品最核心的设定，也是最容易被新读者误读的概念。
                    表面上看，它们是货架、墙面、木板上那些小小的孔洞，是杂货铺随性的装饰；
                    但深入阅读你会发现，洞洞其实承担着至少九层不同的隐喻。
                  </p>
                  <ul className="mt-5 space-y-3">
                    {[
                      "物理入口：连接两个空间的最直接通道",
                      "记忆裂隙：被封存的往事会从洞口渗出",
                      "时空交汇点：不同时间线在洞口短暂重叠",
                      "情感纽带：洞洞连接着彼此挂念的人",
                      "世界观边界：洞洞之外的世界尚未被完全探索",
                    ].map((item, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-3 rounded-xl bg-background-100/70 px-4 py-3 text-sm leading-6 text-foreground-700"
                      >
                        <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-500 text-xs font-medium text-background-50">
                          {i + 1}
                        </span>
                        {item}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-5 text-sm leading-8 text-foreground-700 md:text-base">
                    正是这种「一物多义」的设计，让洞洞从可爱的小细节，
                    逐渐成长为支撑整部漫画世界观的基石。
                  </p>
                </section>

                <section id="shop-space" className="scroll-mt-24">
                  <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                    杂货铺的空间设定
                  </h2>
                  <p className="mt-4 text-sm leading-8 text-foreground-700 md:text-base">
                    杂货铺从外面看只是一间普通的小店，内部却远比表面深远。
                    货架与货架之间藏着普通顾客看不见的通道与夹层，店铺面积甚至
                    会随进入者的心情而改变。每七排货架后，会出现一个标着
                    「非卖品」的暗门；而打烊之后的店，会比白天多出三层楼。
                  </p>
                  <p className="mt-4 text-sm leading-8 text-foreground-700 md:text-base">
                    这种「无限深处」的设定，与经典的阈限空间与无限杂货铺母题一脉相承，
                    也是理解洞洞法则的前提——毕竟，正是这些深不见底的空间，
                    才给了洞洞足够的容身之处。
                  </p>
                </section>

                <section id="time-rules" className="scroll-mt-24">
                  <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                    时间规则与慢一小时的钟
                  </h2>
                  <p className="mt-4 text-sm leading-8 text-foreground-700 md:text-base">
                    挂在店里的那口老钟，永远比真实时间慢一小时。这并非道具上的疏忽，
                    而是整部作品的时间法则：洞洞连接着不同的时间线，当某个时刻
                    恰好与另一个世界的时间「对齐」时，洞口就会短暂开启。
                  </p>
                  <p className="mt-4 text-sm leading-8 text-foreground-700 md:text-base">
                    这也是为什么，店里的常客总在相似的时刻出现，
                    而他们的记忆里，似乎都少了一小段「说不清为什么」的空白。
                  </p>
                </section>

                <section id="character-links" className="scroll-mt-24">
                  <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                    角色关系如何被洞洞串联
                  </h2>
                  <p className="mt-4 text-sm leading-8 text-foreground-700 md:text-base">
                    洞洞不仅是空间设定，更是一张把角色紧密连在一起的关系网。
                    店主「阿洞」是洞洞的倾听者，洞洞猫是唯一的守门者，
                    邮差鸽子先生则负责把消息从一个洞口送到另一个洞口。
                    而失眠人小夜反复寻找的那个洞，正是解开她过去的关键。
                  </p>
                  <p className="mt-4 text-sm leading-8 text-foreground-700 md:text-base">
                    每个人与洞洞的相处方式，都在悄悄透露他与这个世界的真实联系。
                    想知道更多细节，可以前往
                    <a href="/characters" className="mx-1 font-medium text-primary-700 underline underline-offset-4">
                      角色图鉴
                    </a>
                    查看完整档案。
                  </p>
                </section>

                <section id="hidden-clues" className="scroll-mt-24">
                  <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                    隐藏在角落的线索
                  </h2>
                  <p className="mt-4 text-sm leading-8 text-foreground-700 md:text-base">
                    每隔三话就出现的同款绿色玻璃瓶、角落贴纸上反复出现的手写数字、
                    以及那些总在同一位置、却从没人注意到的旧物件——这些不起眼的细节，
                    其实是作者埋下的核心谜题。我们把这些线索按条目整理在了
                    <a href="/settings" className="mx-1 font-medium text-primary-700 underline underline-offset-4">
                      设定百科
                    </a>
                    中，方便考据时随时查阅。
                  </p>
                  <p className="mt-4 text-sm leading-8 text-foreground-700 md:text-base">
                    洞洞杂货铺的魅力，恰恰在于它把深刻的世界观藏进温柔的日常里。
                    每一次重读，都可能发现新的洞、新的线索、新的联系。
                  </p>
                </section>
              </div>
            </div>

            <aside className="hidden lg:block">
              <div className="sticky top-24 rounded-2xl bg-background-100/70 p-5 ring-1 ring-background-200/70">
                <h3 className="font-heading text-sm font-bold text-foreground-950">
                  本文目录
                </h3>
                <ul className="mt-3 space-y-2">
                  {toc.map((item) => (
                    <li key={item.id}>
                      <a
                        href={`#${item.id}`}
                        className="flex items-start gap-2 text-sm leading-5 text-foreground-700 transition-colors hover:text-primary-700"
                      >
                        <i className="ri-circle-fill mt-1.5 text-[5px] text-primary-400"></i>
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>

          <div className="mt-14 border-t border-background-200 pt-8">
            <h3 className="font-heading text-lg font-bold text-foreground-950">
              继续阅读 · 相关解析
            </h3>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {hotArticles.map((item) => (
                <a
                  key={item.id}
                  href={item.link}
                  className="group flex flex-col overflow-hidden rounded-xl bg-background-100/70 ring-1 ring-background-200/70 transition-transform duration-300 hover:-translate-y-1"
                >
                  <div className="h-24 w-full overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-3">
                    <h4 className="text-sm font-semibold leading-5 text-foreground-950 transition-colors group-hover:text-primary-700">
                      {item.title}
                    </h4>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </article>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}